package org.teddapp.project.ui.notes.ink

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.awaitEachGesture
import androidx.compose.foundation.gestures.awaitFirstDown
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawingPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.focusProperties
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.PointerType
import androidx.compose.ui.input.pointer.isSecondaryPressed
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddIconButton
import org.teddapp.project.ui.TeddMenu
import org.teddapp.project.ui.TeddMenuEntry
import org.teddapp.project.ui.TeddSize
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import kotlin.math.hypot

/** Les couleurs d'encre : l'encre du thème, puis cinq couleurs franches. */
private val InkColors = listOf(
    InkStroke.INK,
    0xFFE5484DL, // rouge
    0xFF0B6BCBL, // bleu
    0xFF2B9A66L, // vert
    0xFFF76B15L, // orange
    0xFF8E4EC6L, // violet
)
private const val HIGHLIGHT_YELLOW = 0xFFFFD60AL

/** Trois épaisseurs par outil, en unités de page. */
private val PenWidths = listOf(4f, 8f, 14f)
private val HighlighterWidths = listOf(18f, 30f, 44f)

/** Rayon de la gomme, en unités de page. */
private const val ERASER_RADIUS = 14f

/** Un point plus proche que ça du précédent n'apporte rien au trait, et l'alourdit. */
private const val MIN_STEP = 0.8f

/** L'état de l'éditeur : le dessin, l'outil en main, et l'historique pour annuler. */
internal class InkEditorState(initial: InkDrawing) {
    var drawing by mutableStateOf(initial)
        private set
    val live = mutableStateListOf<InkPoint>()
    var tool by mutableStateOf(InkTool.Pen)
    var erasing by mutableStateOf(false)
    var color by mutableStateOf(InkStroke.INK)
    var width by mutableStateOf(PenWidths[1])

    /** Le doigt dessine-t-il ? Oui tant qu'aucun stylet ne s'est montré ; ensuite il fait défiler. */
    var fingerDraws by mutableStateOf(true)
    var stylusSeen by mutableStateOf(false)

    /** Le dessin a dépassé ce que la note peut porter : le dernier trait est refusé. */
    var full by mutableStateOf(false)

    private val undoStack = ArrayDeque<InkDrawing>()
    private val redoStack = ArrayDeque<InkDrawing>()
    private var version by mutableIntStateOf(0)
    val canUndo: Boolean get() { version; return undoStack.isNotEmpty() }
    val canRedo: Boolean get() { version; return redoStack.isNotEmpty() }

    /** Passe à [next] en gardant l'état d'avant pour Annuler. */
    fun commit(next: InkDrawing, before: InkDrawing = drawing) {
        undoStack.addLast(before)
        if (undoStack.size > 200) undoStack.removeFirst()
        redoStack.clear()
        drawing = next
        version++
    }

    /** Un changement pendant un geste (la gomme qui avance) : on n'empile qu'à la fin. */
    fun preview(next: InkDrawing) { drawing = next }

    fun undo() {
        val prev = undoStack.removeLastOrNull() ?: return
        redoStack.addLast(drawing); drawing = prev; full = false; version++
    }

    fun redo() {
        val next = redoStack.removeLastOrNull() ?: return
        undoStack.addLast(drawing); drawing = next; version++
    }

    fun pick(t: InkTool) {
        erasing = false
        if (t == tool) return
        tool = t
        width = if (t == InkTool.Pen) PenWidths[1] else HighlighterWidths[1]
        if (t == InkTool.Highlighter && color == InkStroke.INK) color = HIGHLIGHT_YELLOW
        if (t == InkTool.Pen && color == HIGHLIGHT_YELLOW) color = InkStroke.INK
    }
}

/**
 * L'éditeur de dessin, en plein écran. Le stylet dessine ; le doigt aussi, jusqu'à ce
 * qu'un stylet se montre, et fait ensuite défiler la page : c'est ce qui permet de poser
 * la main sur l'écran sans laisser de traces. Le bout gomme du stylet, ou son bouton
 * tenu enfoncé, gomme.
 *
 * [budget] est le poids que ce dessin peut atteindre, en caractères encodés ; [onSave]
 * est appelé au fil du dessin et à la fermeture.
 */
@Composable
internal fun InkEditor(
    initial: InkDrawing,
    budget: Int,
    onSave: (InkDrawing) -> Unit,
    onDelete: () -> Unit,
    onClose: (InkDrawing) -> Unit,
    /** Pose le texte lu dans la note ; null là où l'écriture ne se lit pas. */
    onReadText: ((InkDrawing, String, Boolean) -> Unit)? = null,
) {
    val state = remember { InkEditorState(initial) }
    var reading by remember { mutableStateOf(false) }
    var readResult by remember { mutableStateOf<String?>(null) }
    val scope = androidx.compose.runtime.rememberCoroutineScope()
    val read: (() -> Unit)? = onReadText?.let {
        {
            if (!reading && !state.drawing.isEmpty) {
                reading = true
                scope.launch {
                    // La première lecture télécharge le modèle d'écriture : elle peut prendre un moment.
                    readResult = runCatching { HandwritingReader.read(state.drawing) }.getOrNull() ?: ""
                    reading = false
                }
            }
        }
    }
    // Enregistré au fil de l'eau : une appli fermée en plein croquis n'emporte pas le croquis.
    LaunchedEffect(state.drawing) {
        delay(1500)
        onSave(state.drawing)
    }
    Dialog(
        onDismissRequest = { onClose(state.drawing) },
        properties = DialogProperties(usePlatformDefaultWidth = false, dismissOnClickOutside = false),
    ) {
        Column(Modifier.fillMaxSize().background(TeddTokens.Bg).safeDrawingPadding()) {
            InkTopBar(state, onDelete = onDelete, onClose = { onClose(state.drawing) }, onRead = read, reading = reading)
            InkTools(state)
            Box(Modifier.fillMaxWidth().height(1.dp).background(TeddTokens.Line))
            InkPage(state, budget, Modifier.weight(1f))
        }
        readResult?.let { text ->
            ReadTextDialog(
                text = text,
                onPlace = { edited, replace -> readResult = null; onReadText?.invoke(state.drawing, edited, replace) },
                onClose = { readResult = null },
            )
        }
    }
}

/** Ce que la lecture a trouvé : à corriger au besoin, puis à poser dans la note. */
@Composable
private fun ReadTextDialog(text: String, onPlace: (String, Boolean) -> Unit, onClose: () -> Unit) {
    var value by remember(text) { mutableStateOf(text) }
    val shape = RoundedCornerShape(TeddTokens.RLg)
    Dialog(onDismissRequest = onClose, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Column(
            Modifier
                .padding(TeddSpace.S4)
                .widthIn(max = 480.dp)
                .background(TeddTokens.SurfaceOverlay, shape)
                .border(1.dp, TeddTokens.Line, shape)
                .padding(TeddSpace.S5),
            verticalArrangement = Arrangement.spacedBy(TeddSpace.S3),
        ) {
            Text("Texte lu", style = TeddType.Title2, color = TeddTokens.Ink)
            if (text.isBlank()) {
                Text("Rien n'a pu être lu dans ce dessin.", style = TeddType.Body, color = TeddTokens.InkMuted)
            } else {
                androidx.compose.foundation.text.BasicTextField(
                    value = value,
                    onValueChange = { value = it },
                    textStyle = TeddType.BodyLg.copy(color = TeddTokens.Ink),
                    cursorBrush = androidx.compose.ui.graphics.SolidColor(TeddTokens.Ink),
                    modifier = Modifier
                        .fillMaxWidth()
                        .heightIn(min = 96.dp)
                        .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                        .border(1.dp, TeddTokens.LineStrong, RoundedCornerShape(TeddTokens.RSm))
                        .padding(TeddSpace.S3),
                )
            }
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                if (text.isNotBlank()) {
                    TeddButton(text = "Ajouter sous le dessin", onClick = { onPlace(value, false) }, kind = TeddButtonKind.Primary, size = TeddButtonSize.Sm)
                    TeddButton(text = "Remplacer le dessin", onClick = { onPlace(value, true) }, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Sm)
                }
                TeddButton(text = "Fermer", onClick = onClose, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm)
            }
        }
    }
}

@Composable
private fun InkTopBar(state: InkEditorState, onDelete: () -> Unit, onClose: () -> Unit, onRead: (() -> Unit)?, reading: Boolean) {
    var menu by remember { mutableStateOf(false) }
    Row(
        modifier = Modifier.fillMaxWidth().padding(horizontal = TeddSpace.S3, vertical = TeddSpace.S2),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(TeddSpace.S1),
    ) {
        TeddButton(text = "Terminé", onClick = onClose, kind = TeddButtonKind.Primary, size = TeddButtonSize.Sm, icon = "check")
        Spacer(Modifier.weight(1f))
        if (state.full) Text("Dessin plein", style = TeddType.Caption, color = TeddTokens.Danger)
        if (reading) Text("Lecture de l'écriture…", style = TeddType.Caption, color = TeddTokens.InkMuted)
        TeddIconButton(onClick = state::undo, modifier = Modifier.alpha(if (state.canUndo) 1f else 0.35f)) {
            TeddIcon(name = "undo", size = 18.dp, color = TeddTokens.InkMuted)
        }
        TeddIconButton(onClick = state::redo, modifier = Modifier.alpha(if (state.canRedo) 1f else 0.35f)) {
            TeddIcon(name = "redo", size = 18.dp, color = TeddTokens.InkMuted)
        }
        Box {
            TeddIconButton(onClick = { menu = true }, active = menu) {
                TeddIcon(name = "more", size = 16.dp, color = TeddTokens.InkMuted)
            }
            TeddMenu(
                expanded = menu,
                onDismiss = { menu = false },
                width = 250.dp,
                alignEnd = true,
                entries = listOfNotNull(
                    onRead?.let { TeddMenuEntry("Convertir en texte", onClick = it, icon = "edit", hint = "écriture manuscrite") },
                    TeddMenuEntry("Agrandir la page", onClick = { state.commit(state.drawing.taller()) }, icon = "arrow-down"),
                    TeddMenuEntry(
                        "Le doigt dessine",
                        onClick = { state.fingerDraws = !state.fingerDraws },
                        icon = "edit",
                        selected = state.fingerDraws,
                        hint = if (state.stylusSeen) "sinon il fait défiler" else null,
                    ),
                    TeddMenuEntry(
                        "Tout effacer",
                        onClick = { if (!state.drawing.isEmpty) state.commit(state.drawing.with(emptyList())) },
                        icon = "x",
                    ),
                    TeddMenuEntry("Supprimer le dessin", onClick = onDelete, icon = "trash", danger = true),
                ),
            )
        }
    }
}

/** Les outils, les couleurs, les épaisseurs : une bande qui défile sur un écran étroit. */
@Composable
private fun InkTools(state: InkEditorState) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .horizontalScroll(rememberScrollState())
            .padding(horizontal = TeddSpace.S3, vertical = TeddSpace.S1),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(TeddSpace.S1),
    ) {
        ToolButton("edit", "Stylo", active = !state.erasing && state.tool == InkTool.Pen) { state.pick(InkTool.Pen) }
        ToolButton("highlight", "Surligneur", active = !state.erasing && state.tool == InkTool.Highlighter) { state.pick(InkTool.Highlighter) }
        ToolButton("eraser", "Gomme", active = state.erasing) { state.erasing = true }
        Divider()
        InkColors.forEach { c ->
            val shown = if (c == InkStroke.INK) TeddTokens.Ink else Color(c.toInt())
            val selected = !state.erasing && state.color == c
            Box(
                modifier = Modifier
                    .size(36.dp)
                    .clip(CircleShape)
                    .then(if (selected) Modifier.border(2.dp, TeddTokens.Ink, CircleShape) else Modifier)
                    .tap { state.color = c; state.erasing = false },
                contentAlignment = Alignment.Center,
            ) {
                Box(Modifier.size(22.dp).background(shown, CircleShape))
            }
        }
        if (state.tool == InkTool.Highlighter) {
            val selected = !state.erasing && state.color == HIGHLIGHT_YELLOW
            Box(
                modifier = Modifier
                    .size(36.dp)
                    .clip(CircleShape)
                    .then(if (selected) Modifier.border(2.dp, TeddTokens.Ink, CircleShape) else Modifier)
                    .tap { state.color = HIGHLIGHT_YELLOW; state.erasing = false },
                contentAlignment = Alignment.Center,
            ) {
                Box(Modifier.size(22.dp).background(Color(HIGHLIGHT_YELLOW.toInt()), CircleShape))
            }
        }
        Divider()
        val widths = if (state.tool == InkTool.Pen) PenWidths else HighlighterWidths
        widths.forEachIndexed { i, w ->
            val selected = !state.erasing && state.width == w
            Box(
                modifier = Modifier
                    .size(36.dp)
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .background(if (selected) TeddTokens.LimeSoft else Color.Transparent)
                    .tap { state.width = w; state.erasing = false },
                contentAlignment = Alignment.Center,
            ) {
                Box(Modifier.size((4 + i * 4).dp).background(TeddTokens.InkMuted, CircleShape))
            }
        }
    }
}

@Composable
private fun ToolButton(icon: String, label: String, active: Boolean, onClick: () -> Unit) {
    Row(
        modifier = Modifier
            .height(36.dp)
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .background(if (active) TeddTokens.LimeSoft else Color.Transparent)
            .tap(onClick)
            .padding(horizontal = TeddSpace.S2),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        TeddIcon(name = icon, size = 16.dp, color = if (active) TeddTokens.Ink else TeddTokens.InkMuted)
        Text(label, style = TeddType.Caption, color = if (active) TeddTokens.Ink else TeddTokens.InkMuted)
    }
}

/** Un tap sans prendre le focus : un outil ne doit pas voler la main au dessin. */
@Composable
private fun Modifier.tap(onClick: () -> Unit): Modifier = this
    .focusProperties { canFocus = false }
    .clickable(
        interactionSource = remember { androidx.compose.foundation.interaction.MutableInteractionSource() },
        indication = null,
        onClick = onClick,
    )

@Composable
private fun Divider() {
    Box(Modifier.padding(horizontal = 4.dp).width(1.dp).height(20.dp).background(TeddTokens.Line))
}

/**
 * La page. Elle a la largeur de l'écran, bornée à la mesure de lecture, et défile en
 * hauteur. Elle s'allonge seule quand on dessine près du bas.
 */
@Composable
private fun InkPage(state: InkEditorState, budget: Int, modifier: Modifier) {
    val ink = TeddTokens.Ink
    val cache = remember { InkPathCache() }
    val scroll = rememberScrollState()
    BoxWithConstraints(modifier.fillMaxWidth().verticalScroll(scroll), contentAlignment = Alignment.TopCenter) {
        val pageWidth = minOf(maxWidth, TeddSize.Measure + 160.dp)
        Box(
            Modifier
                .padding(vertical = TeddSpace.S4, horizontal = TeddSpace.S2)
                .widthIn(max = pageWidth)
                .fillMaxWidth(),
        ) {
            BoxWithConstraints(Modifier.fillMaxWidth()) {
                val scale = constraints.maxWidth / InkDrawing.WIDTH
                val heightDp = maxWidth * (state.drawing.height / InkDrawing.WIDTH)
                Canvas(
                    Modifier
                        .fillMaxWidth()
                        .height(heightDp)
                        .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RMd))
                        .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RMd))
                        .inkInput(state, scale, budget),
                ) {
                    val strokes = state.drawing.strokes
                    cache.retain(strokes)
                    for (s in strokes) drawStroke(s, cache.pathOf(s, scale), scale, ink)
                    if (state.live.isNotEmpty() && !state.erasing) {
                        val pending = InkStroke(state.tool, state.color, state.width, state.live.toList())
                        drawStroke(pending, buildPath(pending, scale), scale, ink)
                    }
                }
            }
        }
    }
}

/** Ce que le stylet, la souris ou le doigt font sur la page. */
private fun Modifier.inkInput(state: InkEditorState, scale: Float, budget: Int): Modifier = pointerInput(scale) {
    awaitEachGesture {
        val down = awaitFirstDown(requireUnconsumed = false)
        val stylus = down.type == PointerType.Stylus || down.type == PointerType.Eraser
        if (stylus && !state.stylusSeen) {
            // Un stylet s'est montré : désormais le doigt fait défiler, la paume ne dessine plus.
            state.stylusSeen = true
            state.fingerDraws = false
        }
        val draws = stylus || down.type == PointerType.Mouse || state.fingerDraws
        if (!draws) return@awaitEachGesture

        // Le bout gomme du stylet, ou le bouton du S Pen tenu enfoncé, gomme ce geste-là.
        val erase = state.erasing || down.type == PointerType.Eraser ||
            (stylus && currentEvent.buttons.isSecondaryPressed)
        down.consume()
        val before = state.drawing

        fun logical(o: Offset) = Offset(o.x / scale, o.y / scale)
        fun pressureOf(p: Float) = if (stylus) p.coerceIn(0.05f, 1f) else 0.75f

        fun eraseAt(o: Offset) {
            val at = logical(o)
            val kept = state.drawing.strokes.filterNot { s -> hits(s, at) }
            if (kept.size != state.drawing.strokes.size) state.preview(state.drawing.with(kept))
        }

        fun addPoint(o: Offset, p: Float) {
            val at = logical(o)
            val last = state.live.lastOrNull()
            if (last != null && hypot(at.x - last.x, at.y - last.y) < MIN_STEP) return
            state.live.add(InkPoint(at.x, at.y, pressureOf(p)))
        }

        if (erase) eraseAt(down.position) else { state.live.clear(); addPoint(down.position, down.pressure) }

        while (true) {
            val event = awaitPointerEvent()
            val change = event.changes.firstOrNull { it.id == down.id } ?: break
            if (!change.pressed) break
            // Les points intermédiaires que l'écran a captés entre deux images : sans eux,
            // un trait rapide devient une ligne brisée.
            change.historical.forEach { h -> if (erase) eraseAt(h.position) else addPoint(h.position, change.pressure) }
            if (erase) eraseAt(change.position) else addPoint(change.position, change.pressure)
            change.consume()
        }

        if (erase) {
            if (state.drawing !== before) state.commit(state.drawing, before)
            return@awaitEachGesture
        }
        val points = state.live.toList()
        state.live.clear()
        if (points.isEmpty()) return@awaitEachGesture
        val stroke = InkStroke(state.tool, state.color, state.width, points)
        var next = state.drawing.with(state.drawing.strokes + stroke)
        // Près du bas de la page, elle s'allonge d'elle-même.
        if (stroke.bounds[3] > next.height - 150f) next = next.taller()
        if (InkCodec.encode(next).length > budget) {
            state.full = true
            return@awaitEachGesture
        }
        state.commit(next)
    }
}

/** La gomme en [at] touche-t-elle ce trait ? */
private fun hits(s: InkStroke, at: Offset): Boolean {
    val b = s.bounds
    val r = ERASER_RADIUS
    if (at.x < b[0] - r || at.x > b[2] + r || at.y < b[1] - r || at.y > b[3] + r) return false
    val reach = r + s.width / 2f
    val pts = s.points
    if (pts.size == 1) return hypot(pts[0].x - at.x, pts[0].y - at.y) <= reach
    for (i in 1 until pts.size) {
        if (segmentDistance(at, pts[i - 1], pts[i]) <= reach) return true
    }
    return false
}

private fun segmentDistance(p: Offset, a: InkPoint, b: InkPoint): Float {
    val dx = b.x - a.x
    val dy = b.y - a.y
    val len2 = dx * dx + dy * dy
    val t = if (len2 == 0f) 0f else (((p.x - a.x) * dx + (p.y - a.y) * dy) / len2).coerceIn(0f, 1f)
    return hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy))
}
